import { ReactNode, lazy, Suspense, useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingCallButton from "./FloatingCallButton";
import GlobalSchema from "./GlobalSchema";


// Defer non-critical widgets — they don't affect LCP and shouldn't block hydration.
const ChatWidget = lazy(() => import("./ChatWidget"));
const CookieConsent = lazy(() => import("./CookieConsent"));

const useDeferredMount = (delay = 1500) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(() => setReady(true), { timeout: delay });
    } else {
      timeoutId = window.setTimeout(() => setReady(true), delay);
    }
    return () => {
      if (idleId !== undefined && (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback) {
        (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback!(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [delay]);
  return ready;
};

const Layout = ({ children }: { children: ReactNode }) => {
  const deferredReady = useDeferredMount(1500);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Sitewide JSON-LD; per-page tags are handled by SEOHead. */}
      <GlobalSchema />

      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingCallButton />
      {deferredReady && (
        <Suspense fallback={null}>
          <ChatWidget />
          <CookieConsent companyName="We" privacyUrl="/privacy-policy" position="bottom" />
        </Suspense>
      )}
      {/* Spacer for fixed floating call button on mobile */}
      <div className="h-[68px] lg:hidden" />
    </div>
  );
};

export default Layout;
